import {
    type ViewStyle,
    StyleSheet
} from "react-native";
import type {
    SliderMeasuresConstantType,
    SliderColorsConstantType,
    SliderIconConstantType,
    SliderDynamicStyleType,
    SliderMeasures,
    SliderColors,
    SliderSize
} from "./type";
import {
    getTypeColors
} from "../../helpers/theme/typeColors";
import type {
    Mutable
} from "../../types";
import {
    getControlMeasures,
    getHitArea
} from "../../helpers/theme/control";
import {
    getStateColorKey
} from "../../helpers/theme/state";
import {
    getCornerStyle
} from "../../helpers/theme/corner";
import {
    getOpaqueColor,
    webStyle
} from "../../utils";

export const SLIDER_THICKNESS_RATIOS: Record<SliderSize, number> = {
    small: 0.5,
    medium: 1,
    large: 2
};

export const SLIDER_VERTICAL_LENGTH_STEPS = 4;

export const getSliderMeasures = ({
    controlPaddings,
    controlHeights,
    shapeConfigs,
    touchTargets,
    pointerType,
    iconSizes,
    borders,
    size
}: SliderMeasuresConstantType): SliderMeasures => {
    const controlMeasures = getControlMeasures({
        controlPaddings,
        controlHeights,
        shapeConfigs,
        iconSizes,
        size
    });

    const hitArea = getHitArea({
        visualSize: controlMeasures.minHeight,
        pointerType,
        touchTargets
    });

    return {
        trackThickness: borders.indicator * SLIDER_THICKNESS_RATIOS[size],
        thumbSize: Math.max(controlMeasures.iconSize, iconSizes.large),
        labelVariant: controlMeasures.labelVariant,
        iconSize: controlMeasures.iconSize,
        row: hitArea.minHeight
    };
};

export const getSliderColors = ({
    isDisabled,
    colors,
    type
}: SliderColorsConstantType): SliderColors => {
    const typeColors = getTypeColors({
        variant: "filled",
        colors,
        type
    });

    const focusKey = getStateColorKey({
        type
    });

    if(isDisabled) {
        const disabledColor = colors.system.state.content.disabled.neutral;

        const disabledTrackColor = colors.system.state.border.disabled.neutral;

        return {
            pressedRingColor: colors.system.state.overlay.pressed.neutral,
            hoverRingColor: colors.system.state.overlay.hover.neutral,
            thumbColor: getOpaqueColor({
                color: colors.system.state.overlay.disabled.neutral,
                surface: colors.system.default.background
            }),
            thumbBorderColor: disabledTrackColor,
            activeTickColor: disabledColor,
            trackColor: disabledTrackColor,
            tickColor: disabledTrackColor,
            labelColor: disabledColor,
            valueColor: disabledColor,
            fillColor: disabledColor,
            hintColor: disabledColor,
            iconColor: disabledColor,
            focusKey
        };
    }

    return {
        pressedRingColor: colors.system.state.overlay.pressed[focusKey],
        hoverRingColor: colors.system.state.overlay.hover[focusKey],
        trackColor: colors.system.state.overlay.fill.default,
        thumbBorderColor: typeColors.containerColor,
        thumbColor: colors.content.container.raised,
        activeTickColor: typeColors.containerColor,
        fillColor: typeColors.containerColor,
        labelColor: colors.content.text.high,
        valueColor: colors.content.text.mid,
        hintColor: colors.content.text.mid,
        iconColor: colors.content.icon.mid,
        tickColor: colors.content.icon.low,
        focusKey
    };
};

export const getSliderIconStyle = ({
    controlPaddings,
    orientation,
    isEnd
}: SliderIconConstantType): ViewStyle => {
    if(orientation === "vertical") {
        return {};
    }

    if(isEnd) {
        return {
            marginStart: controlPaddings.small
        };
    }

    return {
        marginEnd: controlPaddings.small
    };
};

const stylesheet = StyleSheet.create({
    verticalBody: {
        flexDirection: "column-reverse",
        alignItems: "center"
    },
    body: {
        alignItems: "flex-start",
        flexDirection: "row",
        flexGrow: 1
    },
    icon: {
        justifyContent: "center",
        alignItems: "center"
    },
    horizontalRail: {
        ...webStyle({
            touchAction: "pan-y",
            cursor: "pointer"
        })
    },
    verticalRail: {
        flexGrow: 1,
        ...webStyle({
            touchAction: "pan-x",
            cursor: "pointer"
        })
    },
    verticalHead: {
        justifyContent: "center"
    },
    head: {
        alignItems: "baseline",
        flexDirection: "row"
    },
    rail: {
        ...webStyle({
            userSelect: "none"
        })
    },
    track: {
        pointerEvents: "none",
        position: "absolute",
        overflow: "hidden"
    },
    verticalContainer: {
        alignItems: "center"
    },
    range: {
        position: "absolute"
    },
    value: {
        fontVariant: [
            "tabular-nums"
        ],
        flexShrink: 0
    },
    horizontalRange: {
        height: "100%",
        start: 0
    },
    label: {
        flexShrink: 1,
        flexGrow: 1
    },
    main: {
        flexShrink: 1,
        flexGrow: 1,
        minWidth: 0
    },
    horizontalTrack: {
        start: 0,
        end: 0
    },
    verticalRange: {
        width: "100%",
        start: 0
    },
    verticalTrack: {
        bottom: 0,
        top: 0
    },
    container: {
        minWidth: 0
    }
});

export const useStyles = ({
    controlPaddings,
    currentMeasures,
    controlHeights,
    currentColors,
    shapeConfigs,
    orientation,
    rangeStart,
    rangeSize,
    radiuses,
    spaces
}: SliderDynamicStyleType) => {
    const trackOffset = (currentMeasures.row - currentMeasures.trackThickness) / 2;

    const styles = {
        track: {
            backgroundColor: currentColors.trackColor,
            ...getCornerStyle({
                radius: radiuses.chip,
                shapeConfigs
            })
        } as Mutable<ViewStyle>,
        range: {
            backgroundColor: currentColors.fillColor
        } as Mutable<ViewStyle>,
        body: {} as Mutable<ViewStyle>,
        icon: {} as Mutable<ViewStyle>,
        rail: {} as Mutable<ViewStyle>,
        container: {
            gap: spaces.spacingSm
        } as Mutable<ViewStyle>,
        head: {
            gap: spaces.spacingSm
        } as Mutable<ViewStyle>
    };

    if(orientation === "vertical") {
        Object.assign(styles.body, {
            gap: controlPaddings.small
        });

        Object.assign(styles.icon, {
            width: currentMeasures.row
        });

        Object.assign(styles.rail, {
            minHeight: controlHeights.medium * SLIDER_VERTICAL_LENGTH_STEPS,
            width: currentMeasures.row
        });

        Object.assign(styles.track, {
            width: currentMeasures.trackThickness,
            start: trackOffset
        });

        Object.assign(styles.range, {
            height: rangeSize,
            bottom: rangeStart
        });

        return styles;
    }

    Object.assign(styles.icon, {
        height: currentMeasures.row
    });

    Object.assign(styles.rail, {
        height: currentMeasures.row
    });

    Object.assign(styles.track, {
        height: currentMeasures.trackThickness,
        top: trackOffset
    });

    Object.assign(styles.range, {
        start: rangeStart,
        width: rangeSize
    });

    return styles;
};
export default stylesheet;
